import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser speech recognition, typed by hand because lib.dom does not ship it.
 * Chrome, Edge and Safari (iOS 14.5+) expose it under the webkit prefix;
 * Firefox does not have it at all, so callers must always keep the text box.
 */
interface RecognitionResultEvent {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Speech {
  supported: boolean;
  listening: boolean;
  /** Live transcript while listening, final when it stops. */
  transcript: string;
  error: string | null;
  start(): void;
  stop(): void;
}

export function useSpeech(lang = navigator.language || 'en-US'): Speech {
  const ctor = useRef<RecognitionCtor | null>(recognitionCtor());
  const active = useRef<Recognition | null>(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    active.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = ctor.current;
    if (!Ctor || active.current) return;
    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let text = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result) continue;
        text += result[0]?.transcript ?? '';
      }
      setTranscript(text.trim());
    };
    recognition.onerror = (event) => {
      // "no-speech" and "aborted" are the user changing their mind, not faults.
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(
          event.error === 'not-allowed'
            ? 'Microphone access was blocked. You can still type.'
            : `Voice input failed (${event.error}). You can still type.`,
        );
      }
    };
    recognition.onend = () => {
      active.current = null;
      setListening(false);
    };

    setError(null);
    setTranscript('');
    active.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch {
      active.current = null;
      setListening(false);
    }
  }, [lang]);

  useEffect(() => () => active.current?.abort(), []);

  return { supported: ctor.current !== null, listening, transcript, error, start, stop };
}
